import { OFFER_SOURCE_OF_FUNDS_INDIA } from 'constants/fields';
import type { Field } from 'models/field';
import type { Section, SectionField } from 'models/section';
import type { RootState } from 'reducers/types';
import { getFieldById } from 'selectors/entities/fields/fields';
import { I18n } from '../../../utils/translations/translations';
import { sourceOfFundsIndia } from '../../ui/india/ui';

export type SectionName = 'offers' | 'recipient' | 'sender' | 'academic';
export const SECTION_NAMES: SectionName[] = [
  'offers',
  'recipient',
  'sender',
  'academic',
];

export const populatedFields = (state: RootState, fields: string[]) =>
  fields.map(id => getFieldById(state, id));

export const isSectionPrefilled = (fields: Field[]) => {
  const requiredFields = fields.filter(field => field.required);

  if (requiredFields.length === 0) {
    return true;
  }
  return requiredFields.every(field => field.apiReadOnly || field.prefilled);
};

export const getSectionsByEntities = (
  state: RootState,
  entities: SectionName[],
) => {
  const sections = entities.reduce((accumulator, entity: SectionName) => {
    const sections = getSectionsByEntity(state, entity);
    return accumulator.concat(sections);
  }, [] as Section<SectionField>[]);

  return mergeDuplicatedSections(sections);
};

export const getSectionsByEntity = (state: RootState, entity: SectionName) => {
  const {
    entities: {
      sections: {
        [entity]: { byId: sectionsById, ids: sectionIds },
      },
    },
  } = state;

  return sectionIds.map(id => {
    const section = { ...sectionsById[id] };

    const fields = populatedFields(state, section.fields as string[]);
    const prefilled = isSectionPrefilled(fields);

    return {
      ...section,
      fields,
      prefilled,
    } as Section<SectionField>;
  });
};

export const mergeDuplicatedSections = (sections: Section<SectionField>[]) => {
  return sections.reduce(
    (
      previousSections: Section<SectionField>[],
      section: Section<SectionField>,
    ) => {
      const repeatedSection = (previousSection: Section<SectionField>) =>
        previousSection.name === section.name;
      const previousSection = previousSections.find(repeatedSection);

      if (previousSection) {
        previousSection.fields = [...previousSection.fields, ...section.fields];
        previousSection.prefilled = isSectionPrefilled(
          section.fields as Field[],
        );
      } else {
        previousSections.push(section);
      }

      return previousSections;
    },
    [],
  );
};

export const getSenderConfirmationDetails = (state: RootState, i18n: I18n) => {
  const senderSections = getSectionsByEntity(state, 'sender');
  const offerSections = getSectionsByEntity(state, 'offers');

  senderSections.forEach(senderSection => {
    const foundSection = offerSections.find(offerSection =>
      offerSection.id.includes(senderSection.id),
    );

    if (foundSection) {
      const offerFields = foundSection.fields;
      senderSection.fields = senderSection.fields.concat(offerFields);
    }
    const isOfferSourceOfFunds = senderSection.fields.some(
      field => field.id === OFFER_SOURCE_OF_FUNDS_INDIA,
    );
    if (!isOfferSourceOfFunds) {
      const sourceOfFundsSelected = sourceOfFundsIndia(state);
      if (sourceOfFundsSelected) {
        const sourceOfFundsField = {
          defaultValue: sourceOfFundsSelected,
          id: OFFER_SOURCE_OF_FUNDS_INDIA,
          name: 'source_of_funds_india',
          label: 'Source of Funds',
          type: 'string',
          text: i18n.t(`sourceOfFunds.${sourceOfFundsSelected}.title`),
        } as SectionField;
        senderSection.fields.push(sourceOfFundsField);
      }
    }
  });

  return senderSections;
};
